import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FiMail, FiShield, FiCheck, FiSend } from 'react-icons/fi';
import Loader from 'wrapper/loader';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import authService, { resolveStytchBaseUrl } from 'services/auth/authService';
import useOrganization from 'hooks/useOrganization';
import tokenManager from 'services/auth/tokenManager';
import { company_logo_app } from 'constants/constant';
import { validateEmail as validateEmailUtil } from 'utils/validators.js';
import { buildAppPath } from 'utils/tenant';
import { clearAuthStorage } from 'utils/storage.js';
import styles from './B2BLogin.module.css';

const B2BLogin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  // Use the organization hook for subdomain detection
  const {
    organization: detectedOrg,
    subdomain: detectedSubdomain,
    loading: orgLoading,
    error: orgError,
    isResolved: orgResolved,
    isDevelopment
  } = useOrganization();

  const [email, setEmail] = useState('');
  const [organizationId, setOrganizationId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [errorTimeout, setErrorTimeout] = useState(null);
  const [success, setSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [warningMessage, setWarningMessage] = useState('');
  const [emailError, setEmailError] = useState('');
  const [discoveredOrganizations, setDiscoveredOrganizations] = useState([]);
  const [isDiscovering, setIsDiscovering] = useState(false);
  const [showContactAdmin, setShowContactAdmin] = useState(false);
  const [isMembershipChecking, setIsMembershipChecking] = useState(false);
  const [membershipStatus, setMembershipStatus] = useState(null); // null | 'member' | 'not-member' | 'error'
  const [membershipMessage, setMembershipMessage] = useState('');
  const [membershipConfirmed, setMembershipConfirmed] = useState(false);
  const [redirectMessageKey, setRedirectMessageKey] = useState(0);
  const formRef = useRef(null);
  const skipResolveRef = useRef(false);
  const autoSubmitDoneRef = useRef(false);
  const submitInFlightRef = useRef(false);

  // Track if organization is set from subdomain
  const isSubdomainOrg = useRef(false);
  const redirectTimersRef = useRef([]);

  const clearRedirectFlow = useCallback(() => {
    redirectTimersRef.current.forEach((timerId) => clearTimeout(timerId));
    redirectTimersRef.current = [];
  }, []);

  useEffect(() => {
    return () => {
      clearRedirectFlow();
    };
  }, [clearRedirectFlow]);

  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    const autoEmail = params.get('email');
    const autoSubmit = params.get('autosubmit');
    const resolvedOrgId =
      organizationId ||
      detectedOrg?.stytch_organization_id ||
      detectedOrg?.organization_id ||
      detectedOrg?.id ||
      '';
    if (autoEmail && autoSubmit === 'true' && !autoSubmitDoneRef.current) {
      setEmail(autoEmail);
      // Skip resolver only if tenant context is already resolved on this host.
      skipResolveRef.current = Boolean(resolvedOrgId);
      if (!resolvedOrgId && !orgResolved) return undefined;
      autoSubmitDoneRef.current = true;
      const timer = setTimeout(() => {
        formRef.current?.requestSubmit();
      }, 150);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [location.search, organizationId, detectedOrg, orgResolved]);

  // IMMEDIATE subdomain context setup - set organization ID as soon as we have detectedOrg
  // Prefer stytch_organization_id since that's what the magic link endpoint expects
  useEffect(() => {
    if (detectedOrg && !organizationId) {
      const orgId = detectedOrg.stytch_organization_id || detectedOrg.organization_id || detectedOrg.id;
      if (orgId) {
        if (!import.meta.env.PROD) {
          console.log('[B2BLogin] Setting subdomain context for:', detectedOrg.name, 'orgId:', orgId);
        }
        setOrganizationId(orgId);
        isSubdomainOrg.current = true;
      }
    }
  }, [detectedOrg, organizationId]);

  // Email validation function using the utility
  const validateEmail = useCallback((email) => {
    const isValid = validateEmailUtil(email);
    if (!isValid) {
      setEmailError('Invalid email');
      return false;
    }
    setEmailError('');
    return true;
  }, []);

  // Check membership for subdomain organizations
  const checkMembershipForEmail = useCallback(async (email, orgId) => {
    clearRedirectFlow();
    if (!email || !orgId || !validateEmail(email)) {
      return;
    }

    if (!import.meta.env.PROD) {
      console.log('[DEBUG] Checking membership for email in org');
    }
    setIsMembershipChecking(true);
    setMembershipStatus(null);
    setMembershipMessage('');
    setMembershipConfirmed(false);

    try {
      const response = await authService.checkMembership(email, orgId);
      if (!import.meta.env.PROD) {
        console.log('[DEBUG] Membership check response received');
      }

      if (response.redirect_url) {
        const destination = response.home_branch_slug
          ? `${response.home_branch_slug}.${response.home_organization_slug}`
          : response.home_organization_slug;
        const firstMessage = 'You are not a member of this organization or branch.';
        const secondMessage = destination
          ? `Redirecting you to ${destination}...`
          : 'Redirecting you to your assigned organization or branch...';

        setMembershipStatus('redirecting');
        setMembershipMessage(firstMessage);
        setWarningMessage('');
        setShowContactAdmin(false);
        setMembershipConfirmed(false);
        setRedirectMessageKey((prev) => prev + 1);

        redirectTimersRef.current.push(
          setTimeout(() => {
            setMembershipMessage(secondMessage);
            setRedirectMessageKey((prev) => prev + 1);
          }, 2000)
        );

        redirectTimersRef.current.push(
          setTimeout(() => {
            window.location.replace(response.redirect_url);
          }, 4200)
        );
        return;
      }

      if (response.error) {
        // API failed, fail open for better UX
        if (!import.meta.env.PROD) {
          console.log('[DEBUG] Membership check failed, failing open');
        }
        setMembershipStatus('error');
        setMembershipMessage('');
        setMembershipConfirmed(false);
      } else if (response.is_member) {
        setMembershipStatus('member');
        setMembershipMessage('');
        setWarningMessage('');
        setShowContactAdmin(false);
        setMembershipConfirmed(true);
      } else {
        setMembershipStatus('not-member');
        const orgName = detectedOrg?.name || response.home_organization_name || 'this organization';
        setMembershipMessage(`You are not a member of ${orgName}. Please contact your administrator to request access.`);
        setWarningMessage(`You are not a member of ${orgName}. Please contact your administrator to request access.`);
        setShowContactAdmin(true);
        setMembershipConfirmed(false);
      }
    } catch (error) {
      if (!import.meta.env.PROD) {
        console.error('[DEBUG] Membership check error');
      }
      // Fail open - allow user to proceed
      setMembershipStatus('error');
      setMembershipMessage('');
      setMembershipConfirmed(false);
    } finally {
      setIsMembershipChecking(false);
    }
  }, [validateEmail, detectedOrg]);

  // Organization discovery based on email domain
  const discoverOrganizations = useCallback(async (email) => {
    if (!import.meta.env.PROD) {
      console.log('[DEBUG] discoverOrganizations called');
    }

    if (!validateEmail(email)) {
      return;
    }

    // Skip discovery API call if we already have organization context from subdomain
    if (isSubdomainOrg.current && organizationId) {
      if (!import.meta.env.PROD) {
        console.log('[DEBUG] Skipping organization discovery - already have org context from subdomain');
      }
      setIsDiscovering(false);
      return;
    }

    setIsDiscovering(true);

    try {
      // SUBDOMAIN FIX: Improve organization discovery for subdomain context
      const response = await authService.discoverOrganizations(email);
      if (!import.meta.env.PROD) {
        console.log('[DEBUG] Organization discovery response received');
      }
      
      // Set discovered organizations
      const orgs = response.organizations || [];
      setDiscoveredOrganizations(orgs);
      
      // Automatically select the organization if one is found
      if (orgs.length > 0 && !organizationId) {
        // Prefer stytch_organization_id since that's what the magic link endpoint expects
        const orgId = orgs[0].stytch_organization_id || orgs[0].organization_id || orgs[0].id;
        if (!import.meta.env.PROD) {
          console.log('[DEBUG] Setting organizationId');
        }
        setOrganizationId(orgId);
      }
    } catch (err) {
      if (!import.meta.env.PROD) {
        console.error('Organization discovery failed');
      }
      
      // Handle 422 errors specifically
      if (err.response?.status === 422) {
        if (!import.meta.env.PROD) {
          console.log('[SUBDOMAIN FIX] 422 error - likely subdomain context issue, using fallback');
        }
        // For subdomain contexts, we might already have the org from useOrganization hook
        if (detectedOrg) {
          const orgId = detectedOrg.stytch_organization_id || detectedOrg.organization_id || detectedOrg.id;
          if (orgId) {
            setOrganizationId(orgId);
            setDiscoveredOrganizations([{
              id: orgId,
              name: detectedOrg.name,
              slug: detectedOrg.slug || detectedSubdomain,
              isPlatform: detectedOrg.is_platform
            }]);
          }
        }
      }
      // Don't show error for discovery failure, just proceed
    }
    setIsDiscovering(false);
  }, [organizationId, validateEmail, detectedOrg, detectedSubdomain]);

  // Handle email change with debounced organization discovery
  const handleEmailChange = useCallback((newEmail) => {
    if (!import.meta.env.PROD) {
      console.log('[DEBUG] handleEmailChange called');
    }

    setEmail(newEmail);
    setError('');
    setMembershipStatus(null);
    setMembershipMessage('');
    setWarningMessage('');
    setShowContactAdmin(false);
    setMembershipConfirmed(false);
    clearRedirectFlow();

    // Clear previous timeouts
    if (window.emailDiscoveryTimeout) {
      clearTimeout(window.emailDiscoveryTimeout);
    }
    if (window.membershipCheckTimeout) {
      clearTimeout(window.membershipCheckTimeout);
    }

    // Check if we're in subdomain context
    const isSubdomainFlow = isSubdomainOrg.current && organizationId && detectedOrg;

    // Only trigger discovery/membership when the email looks fully valid
    // (has @ + domain with a dot). Prevents wasteful Stytch API calls
    // for partial input like "john@g" or "john@gma".
    const looksComplete = /^[^@]+@[^@]+\.[^@]+$/.test(newEmail.trim());

    if (looksComplete) {
      if (isSubdomainFlow) {
        // For subdomain flow, check membership instead of discovering orgs
        if (!import.meta.env.PROD) {
          console.log('[DEBUG] Subdomain flow detected, will check membership');
        }
        window.membershipCheckTimeout = setTimeout(() => {
          checkMembershipForEmail(newEmail, organizationId);
        }, 600);
      } else {
        // For non-subdomain flow, discover organizations
        window.emailDiscoveryTimeout = setTimeout(() => {
          if (isSubdomainOrg.current && organizationId) {
            return;
          }
          discoverOrganizations(newEmail);
        }, 600);
      }
    } else {
      // Clear states when email is incomplete
      if (!isSubdomainFlow) {
        setDiscoveredOrganizations([]);
        setOrganizationId('');
      }
    }
  }, [discoverOrganizations, checkMembershipForEmail, organizationId, detectedOrg]);

  useEffect(() => {
    // Check for error from callback redirect
    const urlParams = new URLSearchParams(window.location.search);
    const callbackError = urlParams.get('error');
    const orgSlug = urlParams.get('org') || urlParams.get('queryorg');

    // Display error from authentication callback
    if (callbackError) {
      if (!import.meta.env.PROD) {
        console.log('[B2BLogin] Displaying error from callback');
      }
      setError(decodeURIComponent(callbackError));

      // Also check for stored error details
      const storedError = sessionStorage.getItem('auth_error');
      if (storedError) {
        try {
          const errorDetails = JSON.parse(storedError);
          if (!import.meta.env.PROD) {
            console.log('[B2BLogin] Additional error details found');
          }
          // Clear the stored error after displaying
          sessionStorage.removeItem('auth_error');
        } catch (e) {
          if (!import.meta.env.PROD) {
            console.error('[B2BLogin] Failed to parse stored error');
          }
        }
      }

      // Clear error from URL to prevent it from persisting on refresh
      const newUrl = new URL(window.location);
      newUrl.searchParams.delete('error');
      window.history.replaceState({}, document.title, newUrl.pathname + newUrl.search);
    }

    // If we have a detected organization from subdomain, use it
    if (detectedOrg && orgResolved) {
      if (!import.meta.env.PROD) {
        console.log('Organization detected from subdomain');
      }

      // Set the organization if subdomain was detected
      // Prefer stytch_organization_id since that's what the magic link endpoint expects
      if (!organizationId && detectedOrg) {
        const orgId = detectedOrg.stytch_organization_id || detectedOrg.organization_id || detectedOrg.id;
        if (orgId) {
          setOrganizationId(orgId);
          isSubdomainOrg.current = true;
          // For subdomain flows, we don't need discoveredOrganizations
          // The organization context is already set, button logic should work without it
          setDiscoveredOrganizations([]);
        }
      }
    }

    // If we only have query org slug and no tenant host context, keep discovery flow.
    if (orgSlug && orgResolved && !detectedOrg) return;
  }, [detectedOrg, detectedSubdomain, orgResolved, organizationId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitInFlightRef.current) return;
    submitInFlightRef.current = true;

    if (!import.meta.env.PROD) {
      console.log('[DEBUG] Form submission started');
    }

    // Clear any existing error timeout
    if (errorTimeout) {
      clearTimeout(errorTimeout);
      setErrorTimeout(null);
    }

    setError('');
    setSuccessMessage('');
    setWarningMessage('');
    setShowContactAdmin(false);
    setEmailError('');
    setLoading(true);

    const trimmedEmail = (email || '').trim();

    // Validate email
    if (!validateEmail(trimmedEmail)) {
      setLoading(false);
      submitInFlightRef.current = false;
      return;
    }

    try {
      // Skip resolve-login when we already know the user is B2B:
      // - subdomain context means they landed on a tenant-specific login
      // - skipResolveRef is set by certain redirect flows
      // This saves ~600ms on the submit path.
      const shouldSkipResolve = skipResolveRef.current || (isSubdomainOrg.current && organizationId);
      if (skipResolveRef.current) {
        skipResolveRef.current = false;
      }

      if (!shouldSkipResolve) {
        const resolved = await authService.resolveLogin(trimmedEmail);
        const resolvedType = resolved?.user_type;
        const redirectUrl = resolved?.redirect_url;
        if (redirectUrl) {
          let shouldRedirect = resolvedType === 'consumer';
          try {
            const target = new URL(redirectUrl);
            if (target.host !== window.location.host) shouldRedirect = true;
          } catch (_err) {
            // ignore parse failures and continue with current host
          }
          if (shouldRedirect) {
            const target = new URL(redirectUrl);
            target.searchParams.set('email', trimmedEmail);
            target.searchParams.set('autosubmit', 'true');
            window.location.replace(target.toString());
            return;
          }
        }
      }

      const effectiveOrgId =
        organizationId ||
        detectedOrg?.stytch_organization_id ||
        detectedOrg?.organization_id ||
        detectedOrg?.id ||
        '';

      if (!effectiveOrgId) {
        setError('Please select an organization to continue');
        setLoading(false);
        submitInFlightRef.current = false;
        return;
      }

      // Use canonical base URL that matches Stytch configuration
      const canonicalBase = resolveStytchBaseUrl();

      const redirectUrl = `${canonicalBase}/auth/callback?flow=b2b`;

      // Ensure we never accidentally route to platform callback
      if (!import.meta.env.PROD && redirectUrl.includes('/platform/auth/callback')) {
        console.error('[AUTH FIX] Prevented platform callback routing for client authentication');
      }

      // Send magic link with proper organization context
      // Both login and signup will use the same redirect URL as configured in Stytch
      if (!import.meta.env.PROD) {
        console.log('[DEBUG] About to send magic link');
      }

      const response = await authService.sendB2BMagicLink(
        trimmedEmail,
        effectiveOrgId,
        redirectUrl,  // Use same URL for login
        redirectUrl   // Use same URL for signup (as configured in Stytch)
      );

      if (!import.meta.env.PROD) {
        console.log('[DEBUG] Magic link response received');
      }

      // Check the response structure from the backend
      // authService returns the data directly from axios response
      if (response) {
        if (response.success === true) {
          // Member found - magic link sent successfully
          sessionStorage.setItem('mira_awaiting_magic_link', 'true');
          setSuccess(true);
          setSuccessMessage(response.message || '✅ Magic link sent! Check your email for the secure sign-in link.');
          setWarningMessage('');
          setError('');
        } else if (response.success === false) {
          // Non-member or other controlled failure
          if (response.display_type === 'warning') {
            // Show as warning (orange/amber) for non-members
            setWarningMessage(response.message);
            setSuccessMessage('');
            setError('');
            // Show contact admin help if indicated
            if (response.next_step === 'contact_admin') {
              setShowContactAdmin(true);
            }
          } else {
            // Show as error for other failures
            setError(response.message || 'Failed to send magic link. Please try again.');
            setSuccessMessage('');
            setWarningMessage('');
          }
        } else {
          // Fallback for old response format (just success without explicit field)
          setSuccess(true);
          setSuccessMessage('✅ Magic link sent! Check your email for the secure sign-in link.');
        }
      } else {
        setError('Failed to send magic link. Please try again.');
      }
    } catch (err) {
      if (!import.meta.env.PROD) {
        console.error('[B2BLogin] Magic link error');
      }
      // Provide more specific error messages
      let errorMessage = 'An error occurred. Please try again or contact support.';

      if (err.response?.status === 404) {
        errorMessage = 'Organization not found. Please contact your administrator.';
      } else if (err.response?.status === 403) {
        errorMessage = 'You are not authorized to access this organization.';
      } else if (err.response?.status === 500) {
        errorMessage = 'Server error. Please wait a moment and try again.';
      } else if (err.response?.status === 422) {
        errorMessage = 'Invalid request. Please check your email and try again.';
      } else if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);

      // Auto-clear error after 10 seconds for better UX
      const timeout = setTimeout(() => {
        setError('');
        setErrorTimeout(null);
      }, 10000);
      setErrorTimeout(timeout);
    } finally {
      setLoading(false);
      submitInFlightRef.current = false;
    }
  };

  const handleBack = () => {
    navigate(buildAppPath('/'));
  };

  if (success) {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <div className={styles.header}>
            <img
              src={company_logo_app || '/opus-wordmark-dark.png'}
              alt="Opus Capital Markets Consultants"
              className={styles.logo}
            />
          </div>

          <div className={styles.successContent}>
            <div className={styles.successIcon}>
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C13.1 2 14 2.9 14 4C14 5.1 13.1 6 12 6C10.9 6 10 5.1 10 4C10 2.9 10.9 2 12 2ZM21 9V7L15 1L14.5 1.5C14.2 1.8 14 2.2 14 2.7V4.2C14 4.7 14.2 5.1 14.5 5.4L15 5.9V7C15 7.6 15.4 8 16 8S17 7.6 17 7V6.9L19.5 9.4C19.8 9.7 20.2 9.9 20.7 9.9C21.2 9.9 21.6 9.7 21.9 9.4C22.2 9.1 22.4 8.7 22.4 8.2C22.4 7.7 22.2 7.3 21.9 7L21 9ZM20 14L20 18C20 19.1 19.1 20 18 20H6C4.9 20 4 19.1 4 18V6C4 4.9 4.9 4 6 4H10V2H6C3.8 2 2 3.8 2 6V18C2 20.2 3.8 22 6 22H18C20.2 22 22 20.2 22 18V14H20Z" />
              </svg>
            </div>
            <h2 className={styles.title}>Magic link sent!</h2>
            <p className={styles.description}>
              We've sent a secure sign-in link to<br />
              <strong className={styles.emailHighlight}>{email}</strong>
            </p>
            <p className={styles.subtitle}>
              Click the link in your email to access your account. The link will expire in 15 minutes for security.
            </p>

            {/* Organization info */}
            {organizationId && discoveredOrganizations.length > 0 && (
              <div className={styles.orgInfo}>
                <p className={styles.orgLabel}>Signing in to:</p>
                <p className={styles.orgName}>
                  {discoveredOrganizations.find(org => org.id === organizationId)?.name}
                  {discoveredOrganizations.find(org => org.id === organizationId)?.isPlatform &&
                    <span className={styles.platformBadge}>Platform</span>
                  }
                </p>
              </div>
            )}

            <div className={styles.helpSection}>
              <h3 className={styles.helpTitle}>Need help?</h3>
              <div className={styles.helpItems}>
                <div className={styles.helpItem}>
                  <span className={styles.helpIcon}>📧</span>
                  <span>Check your spam/junk folder</span>
                </div>
                <div className={styles.helpItem}>
                  <span className={styles.helpIcon}>⏱️</span>
                  <span>Wait up to 2 minutes for delivery</span>
                </div>
                <div className={styles.helpItem}>
                  <span className={styles.helpIcon}>🔒</span>
                  <span>Link expires in 15 minutes</span>
                </div>
              </div>
            </div>

            <div className={styles.actions}>
              <button
                onClick={() => {
                  setSuccess(false);
                  setEmail('');
                  setOrganizationId('');
                  setDiscoveredOrganizations([]);
                  setWarningMessage('');
                  setShowContactAdmin(false);
                }}
                className={styles.tryAgainButton}
              >
                Try different email
              </button>
              <button
                onClick={() => handleSubmit({ preventDefault: () => { } })}
                disabled={loading}
                className={styles.resendButton}
              >
                {loading ? 'Sending...' : 'Resend link'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show loading state while organization is being resolved
  if (orgLoading && !orgResolved) {
    return <Loader message="Detecting organization..." />;
  }

  // Show error if organization lookup failed
  if (orgError && orgResolved) {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <div className={styles.header}>
            <img
              src={company_logo_app || '/opus-wordmark-dark.png'}
              alt="Opus Capital Markets Consultants"
              className={styles.logo}
            />
          </div>
          <div className={styles.content}>
            <div className={styles.errorContainer}>
              <div className={styles.errorIcon}>
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
                </svg>
              </div>
              <h2 className={styles.errorTitle}>Organization Not Found</h2>
              <p className={styles.errorDescription}>
                We couldn't find an organization for this subdomain.
                Please check the URL or contact your administrator.
              </p>
              <button
                onClick={() => window.location.href = '/'}
                className={styles.submitButton}
              >
                Go to Main Login
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <div className={styles.header}>
          <img
            src={company_logo_app || '/opus-wordmark-dark.png'}
            alt="Opus Capital Markets Consultants"
            className={styles.logo}
          />
        </div>

        <div className={styles.content}>
          {/* Show organization info if detected from subdomain */}
          {detectedOrg && (
            <div className={styles.organizationHeader}>
              {detectedOrg.logo_url && (
                <img
                  src={detectedOrg.logo_url}
                  alt={`${detectedOrg.name} logo`}
                  className={styles.organizationLogo}
                />
              )}
              <h2 className={styles.organizationName}>
                Sign in to {detectedOrg.name || 'Your Organization'}
              </h2>
              <p className={styles.organizationSubtext}>
                {detectedOrg.is_platform
                  ? 'Platform Administrator Access'
                  : 'Enter your email to receive a secure sign-in link'}
              </p>
            </div>
          )}

          {!detectedOrg && (
            <>
              <h2 className={styles.title}>Sign in as Loan Officer</h2>
              <p className={styles.description}>
                Enter your email to receive a secure sign-in link
              </p>
            </>
          )}

          {/* Security Notice */}
          <div className={styles.securityNotice}>
            <svg className={styles.securityIcon} viewBox="0 0 24 24" fill="currentColor">
              <path d="M12,1L3,5V11C3,16.55 6.84,21.74 12,23C17.16,21.74 21,16.55 21,11V5L12,1M10,17L6,13L7.41,11.59L10,14.17L16.59,7.58L18,9L10,17Z" />
            </svg>
            <div className={styles.securityText}>
              <strong>Enterprise Security:</strong> All magic links are encrypted and expire after 15 minutes.
              NMLS compliant with financial industry regulations.
            </div>
          </div>

          <form ref={formRef} onSubmit={handleSubmit} className={styles.form}>
            <div className={styles.inputGroup}>
              <label htmlFor="email" className={styles.label}>
                Email Address
              </label>
              <div className={styles.inputWrapper}>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => {
                    console.log('[DEBUG INPUT] onChange event triggered');
                    console.log('[DEBUG INPUT] e.target.value:', e.target.value);
                    console.log('[DEBUG INPUT] e.target.value raw:', JSON.stringify(e.target.value));
                    handleEmailChange(e.target.value);
                  }}
                  onBlur={() => email && validateEmail(email)}
                  placeholder="your.email@company.com"
                  className={`${styles.input} ${emailError ? styles.inputError : ''}`}
                  disabled={loading}
                  autoFocus
                  required
                  aria-describedby={emailError ? "email-error" : undefined}
                />
                {(isDiscovering || isMembershipChecking) && (
                  <div className={styles.loadingSpinner} aria-label={isMembershipChecking ? "Verifying membership" : "Discovering organizations"}>
                    <Loader variant="button" />
                  </div>
                )}
              </div>
              {emailError && (
                <div className={styles.fieldError} id="email-error" role="alert">
                  {emailError}
                </div>
              )}
              {email && discoveredOrganizations.length > 0 && !detectedOrg && (
                <div className={styles.helperText}>
                  ✓ Found {discoveredOrganizations.length} organization{discoveredOrganizations.length > 1 ? 's' : ''} for your domain
                </div>
              )}
              {/* Membership status indicator for subdomain flow */}
              {detectedOrg && membershipConfirmed && membershipStatus === 'member' && email && (
                <div className={styles.helperText} style={{ color: 'green' }}>
                  ✓ Membership verified
                </div>
              )}
              {detectedOrg && isMembershipChecking && email && (
                <div className={styles.helperText}>
                  Verifying membership...
                </div>
              )}
            </div>

            {/* Organization display - Show when organizations are discovered via email */}
            {/* SECURITY FIX: Hide organization dropdown when using query parameter */}
            {discoveredOrganizations.length > 0 &&
              !detectedOrg &&
              organizationId &&
              !localStorage.getItem('hide_org_name') && (
                <div className={styles.inputGroup}>
                  <label htmlFor="organization" className={styles.label}>
                    Organization
                  </label>
                  {/* For single organization, just show as read-only */}
                  {discoveredOrganizations.length === 1 ? (
                    <>
                      <div className={styles.detectedOrgField}>
                        <input
                          type="text"
                          id="organization"
                          value={discoveredOrganizations[0].name || 'Organization'}
                          className={styles.input}
                          disabled
                          readOnly
                        />
                        <span className={styles.detectedBadge}>✓ Detected</span>
                      </div>
                      <p className={styles.helperText}>
                        Organization detected from your email domain
                      </p>
                    </>
                  ) : (
                    /* For multiple organizations, show selector */
                    <>
                      <select
                        id="organization"
                        value={organizationId}
                        onChange={(e) => setOrganizationId(e.target.value)}
                        className={styles.select}
                        disabled={loading || isDiscovering}
                        required
                        aria-describedby="organization-helper"
                      >
                        <option value="">Select your organization...</option>
                        {discoveredOrganizations.map(org => (
                          <option key={org.stytch_organization_id || org.id} value={org.stytch_organization_id || org.id}>
                            {org.name} {org.isPlatform ? '(Platform)' : ''}
                          </option>
                        ))}
                      </select>
                      <p className={styles.helperText} id="organization-helper">
                        Multiple organizations found for your domain - please select one
                      </p>
                    </>
                  )}
                </div>
              )}

            {/* Show organization context indicator */}
            {/* When organization is detected via subdomain, show the name */}
            {detectedOrg && detectedOrg.stytch_organization_id && (
              <div className={styles.inputGroup}>
                <label htmlFor="organization-detected" className={styles.label}>
                  Organization
                </label>
                <div className={styles.detectedOrgField}>
                  <input
                    type="text"
                    id="organization-detected"
                    value={detectedOrg.name || 'Organization'}
                    className={styles.input}
                    disabled
                    readOnly
                  />
                  <span className={styles.detectedBadge}>✓ Auto-detected</span>
                </div>
                <p className={styles.helperText}>
                  Organization detected from your access URL
                </p>
              </div>
            )}

            {/* When organization is set via query param, show generic indicator (SECURITY) */}
            {!detectedOrg && organizationId && window.location.search.includes('org=') && (
              <div className={styles.inputGroup}>
                <div className={styles.securityNotice} style={{ marginTop: '0.5rem' }}>
                  <svg className={styles.securityIcon} viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12,1L3,5V11C3,16.55 6.84,21.74 12,23C17.16,21.74 21,16.55 21,11V5L12,1M10,17L6,13L7.41,11.59L10,14.17L16.59,7.58L18,9L10,17Z" />
                  </svg>
                  <div className={styles.securityText}>
                    <strong>Organization Context Set:</strong> You are signing into your organization's workspace.
                  </div>
                </div>
              </div>
            )}

            {/* Show organization discovery status */}
            {email && !isDiscovering && discoveredOrganizations.length === 0 && email.includes('@') && !detectedOrg && (
              <div className={styles.organizationStatus}>
                <div className={styles.noOrgFound}>
                  <svg className={styles.infoIcon} viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" />
                  </svg>
                  <div>
                    <p className={styles.noOrgTitle}>No organizations found</p>
                    <p className={styles.noOrgText}>
                      We couldn't find any organizations associated with your email domain.
                      You may need to contact your administrator to set up your account.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {error && (
              <div className={styles.error} role="alert" aria-live="assertive">
                <div className={styles.errorContent}>
                  <svg className={styles.errorIcon} viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
                  </svg>
                  <span className={styles.errorText}>{error}</span>
                </div>
                {/* Show retry suggestion for specific errors */}
                {(error.includes('timing') || error.includes('try again') || error.includes('500')) && (
                  <div className={styles.errorHelp}>
                    <p>💡 <strong>Tip:</strong> If this is your first login, please wait a moment and request a new magic link.</p>
                  </div>
                )}
              </div>
            )}

            {/* Show membership warning for subdomain non-members */}
            {membershipStatus === 'redirecting' && membershipMessage && (
              <div className={`${styles.warning} ${styles.fadeMessage}`} key={redirectMessageKey}>
                ⚠️ {membershipMessage}
              </div>
            )}

            {membershipStatus === 'not-member' && membershipMessage && (
              <div className={styles.warning}>
                ⚠️ {membershipMessage}
                <div className={styles.contactAdminHelp}>
                  <p>To gain access to this organization:</p>
                  <ul>
                    <li>Contact your organization administrator</li>
                    <li>Request to be added as a member</li>
                    <li>Or email support at <a href="mailto:support@mira.com">support@mira.com</a></li>
                  </ul>
                </div>
              </div>
            )}

            {/* Show other warnings */}
            {warningMessage && membershipStatus !== 'not-member' && (
              <div className={styles.warning}>
                ⚠️ {warningMessage}
                {showContactAdmin && (
                  <div className={styles.contactAdminHelp}>
                    <p>To gain access to this organization:</p>
                    <ul>
                      <li>Contact your organization administrator</li>
                      <li>Request to be added as a member</li>
                      <li>Or email support at <a href="mailto:support@mira.com">support@mira.com</a></li>
                    </ul>
                  </div>
                )}
              </div>
            )}

            {successMessage && !success && (
              <div className={styles.success}>
                {successMessage}
              </div>
            )}

            <button
              type="submit"
              disabled={loading || !email || !organizationId || membershipStatus === 'not-member' || isMembershipChecking}
              className={styles.submitButton}
            >
              {loading ? (
                <span className={styles.buttonLoadingContent}>
                  <Loader variant="button" />
                  Sending...
                </span>
              ) : (
                'Send Magic Link'
              )}
            </button>
          </form>

          <div className={styles.divider}>
            <span>or</span>
          </div>

          {!detectedOrg && (
            <button
              onClick={handleBack}
              className={styles.backButton}
            >
              Back to account selection
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default B2BLogin;
